// src/app/core/components/MainPage.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, TrendingUp, Zap } from "lucide-react";
import { motion } from "motion/react";
import { PromptComposerBlock } from "@/app/core/mainPage/prompt/prompt-composer";
import { QuickPromptChipsBlock } from "@/app/core/mainPage/prompt/quick-prompts";
import { PromptExamplesBlock } from "@/app/core/mainPage/prompt/prompt-examples";
import { HomePromptComposerPageController } from "./mainPagecomp/HomePromptComposerPageController";
import { QuickPromptsPageController } from "./mainPagecomp/QuickPromptsPageController";
import { PromptExamplesPageController } from "./mainPagecomp/PromptExamplesPageController";
import { CreateModal } from "./CreateModal";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { NewsSection } from "./mainPagecomp/News/NewsSection";
import { NewsModal } from "./mainPagecomp/News/NewsModal";
import { newsItems } from "./mainPagecomp/News/configNews/news";
import { TopModelsSection } from "./topModelsSection/TopModelsSection";
import {
  MOCK_TEXT_MODEL,
  MOCK_IMAGE_MODEL,
  MOCK_VIDEO_MODEL,
  MOCK_LATEST_MODEL,
} from "./topModelsSection/dataload/mockData";
import { VideoSection } from "./VideoSection";
import { MOCK_VIDEO_MODELS } from "./VideoSection/dataLoad/mockData";

export const MainPage = () => {
  const navigate = useNavigate();
  const { t, tRaw } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<typeof newsItems[0] | null>(null);
  const [ready, setReady] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerController = useMemo(() => new HomePromptComposerPageController(navigate), [navigate]);
  const quickPromptsController = useMemo(() => new QuickPromptsPageController(), []);
  const promptExamplesController = useMemo(() => new PromptExamplesPageController(), []);
  

  useEffect(() => {
    const timeout = setTimeout(() => {
      setReady(true);
      document.body.classList.add('loaded');
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  const isComposerLoading = false;
  const isQuickPromptsLoading = false;
  const isExamplesLoading = false;

  // Приветствие пользователя
  const tgUser = (window as any).Telegram?.WebApp?.initDataUnsafe?.user;
  const firstName = tgUser?.first_name || '';
  const lastName = tgUser?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Пользователь';

  // Динамическое приветствие по времени суток
  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) return t('goodMorning');
    if (hour >= 12 && hour < 18) return t('goodAfternoon');
    if (hour >= 18 && hour < 23) return t('goodEvening');
    return t('goodNight');
  };

  const GALLERY_EXAMPLES = tRaw('galleryExamples');
  const TEXT_EXAMPLES = tRaw('textExamples');
  const QUICK_PROMPTS = tRaw('quickPrompts');
  const randomVideoModel = useMemo(() => {
    const idx = Math.floor(Math.random() * MOCK_VIDEO_MODELS.length);
    return MOCK_VIDEO_MODELS[idx];
  }, []);

  useEffect(() => {
    composerController.syncTextareaHeight(textareaRef.current);
  }, [composerController, prompt]);

  const handleSubmit = () => {
    console.log('[MainPage] handleSubmit called, prompt:', prompt);
    composerController.submit(prompt);
  };

  const handleTryPrompt = (nextPrompt: string) => {
    composerController.applyQuickPrompt(nextPrompt, () => textareaRef.current?.focus(), setPrompt);
  };

  const handleCopy = async (value: string, id: string) => {
    await composerController.copyPrompt(value, id, setCopiedId);
  };

  const animationProps = ready
    ? {
        initial: { opacity: 0, y: 14 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.35 },
      }
    : {};

  return (
    <div id="main-page" className="relative h-full overflow-y-auto" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="relative z-10">
        <div className="pb-20">
          {/* Hero-секция с приветствием */}
          <div className="relative px-4 py-12">
            <motion.div
              initial={ready ? { opacity: 0, y: 20 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 text-center"
            >
              <div className="mb-4 inline-flex items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/40 dark:bg-white/20 backdrop-blur-md border border-white/50 dark:border-white/30 shadow-[0_0_15px_rgba(139,92,246,0.3)] overflow-hidden">
                  <img src="/logo.jpg" alt="Логотип" className="h-10 w-10 rounded-full object-cover ring-2 ring-white/50 shadow-lg" />
                </div>
              </div>
              <h1 className="mb-3 text-3xl font-bold md:text-4xl text-foreground drop-shadow-lg" style={{ minHeight: '2.5em' }}>
                {getTimeGreeting()}, {fullName}!
              </h1>
              <p className="mx-auto max-w-2xl text-base md:text-lg text-muted-foreground drop-shadow-md">
                {t('generateTextsImages')}
              </p>
            </motion.div>
            <PromptComposerBlock
              isLoading={isComposerLoading}
              prompt={prompt}
              textareaRef={textareaRef}
              onPromptChange={setPrompt}
              onSubmit={handleSubmit}
            />
          </div>

          {/* Новостной блок */}
          <NewsSection onNewsClick={setSelectedNews} />

          {/* Топ-модели */}
          <TopModelsSection
            textModel={MOCK_TEXT_MODEL}
            imageModel={MOCK_IMAGE_MODEL}
            videoModel={MOCK_VIDEO_MODEL}
            latestModel={MOCK_LATEST_MODEL}
            isLoading={false}
            onModelClick={(modelId, category) => {
              navigate("/text-generation", {
                state: {
                  initialTab: "models" as const,
                  selectedModelId: modelId,
                  modelCategory: category,
                },
              });
            }}
          />

          {/* Галерея изображений */}
          <section className="px-4 py-6">
            <motion.div {...animationProps} className="mx-auto max-w-7xl">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-xl font-bold text-foreground drop-shadow text-center">
                  {t('gallery')}
                </h2>
              </div>
              <PromptExamplesBlock
                isLoading={isExamplesLoading}
                featuredExamples={[]}
                galleryExamples={GALLERY_EXAMPLES}
                textExamples={[]}
                copiedId={copiedId}
                onCopy={(text, id) => promptExamplesController.copyPrompt(text, id, handleCopy)}
                onSelect={(value) => promptExamplesController.selectPrompt(value, handleTryPrompt)}
              />
            </motion.div>
          </section>

          <VideoSection
            model={randomVideoModel}
            isLoading={false}
            onModelClick={(modelId) => {
              navigate("/text-generation", {
                state: {
                  initialTab: "models" as const,
                  selectedModelId: modelId,
                  modelCategory: "video",
                },
              });
            }}
          />

          {/* Примеры текстов */}
          <section className="px-4 py-3">
            <motion.div {...animationProps} className="mx-auto max-w-7xl">
              <h2 className="mb-0 text-xl font-bold text-center text-foreground drop-shadow">
                {t('textExamplesTitle')}
              </h2>
              <div style={{ marginTop: -23 }}>
                <PromptExamplesBlock
                  isLoading={isExamplesLoading}
                  featuredExamples={[]}
                  galleryExamples={[]}
                  textExamples={TEXT_EXAMPLES}
                  copiedId={copiedId}
                  onCopy={(text, id) => promptExamplesController.copyPrompt(text, id, handleCopy)}
                  onSelect={(value) => promptExamplesController.selectPrompt(value, handleTryPrompt)}
                  className="mb-0"
                />
              </div>
            </motion.div>
          </section>

          {/* Быстрые подсказки */}
          <section className="px-4 py-6">
            <motion.div {...animationProps} className="mx-auto max-w-7xl">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-xl font-bold text-foreground drop-shadow">{t('tryThis')}</h2>
                <button
                  type="button"
                  onClick={() => handleCopy(prompt || QUICK_PROMPTS[0], "composer-copy")}
                  className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-medium bg-accent/20 text-foreground hover:bg-accent/30 transition-all"
                >
                  <Copy className="h-3.5 w-3.5" /> {t('copyPrompt')}
                </button>
              </div>
              <QuickPromptChipsBlock
                isLoading={isQuickPromptsLoading}
                prompts={QUICK_PROMPTS}
                onSelect={(value) => quickPromptsController.selectPrompt(value, handleTryPrompt)}
              />
            </motion.div>
          </section>

          {/* CTA */}
          <section className="px-4 pb-8 pt-6">
            <motion.div
              {...animationProps}
              className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl px-8 py-12 text-center text-white shadow-2xl"
              style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
            >
              <TrendingUp className="mx-auto mb-4 h-12 w-12" />
              <h2 className="mb-3 text-2xl font-bold md:text-3xl drop-shadow-lg">{t('startCreating')}</h2>
              <p className="mx-auto mb-6 max-w-xl text-white/90 drop-shadow">{t('joinThousands')}</p>
              <button
                type="button"
                onClick={() => textareaRef.current?.focus()}
                className="rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-[#764ba2] hover:bg-gray-100 transition-colors shadow-lg"
              >
                {t('tryFree')}
              </button>
              <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
            </motion.div>
            <div className="sr-only"><Zap /></div>
          </section>
        </div>

        <CreateModal open={modalOpen} onClose={() => setModalOpen(false)} />
      </div>

      {selectedNews && (
        <NewsModal news={selectedNews} onClose={() => setSelectedNews(null)} />
      )}
    </div>
  );
};